import {Point} from 'pixi.js';
import {random as _random} from 'lodash/number';
import {assign as _extend} from 'lodash/object';
import {noop as _noop} from 'lodash/util';
import Utils from '../libs/utils';
import Character from './Character';
import sound from './Sound';

const AWAY_ANIMATION_SECONDS = 0.6;
const RANDOM_ROUTE_DELTA = 300;


class Tram extends Character {
  /**
   * Tram constructor
   * Method to instantiate a new Tram character
   * @param {Object} options with various tram configuration values
   * @param {String} options.colorProfile String that is concatinated with `tram/` to generate the sprite ID
   * @param {String} options.spritesheet The object property to ask PIXI's resource loader for
   * @param {Number} [options.maxX] When randomly flying, imposes an upper bound on the X coordinate
   * @param {Number} [options.maxY] When randomly flying, imposes an upper bound on the Y coordinate
   * @param {Number} [options.randomRouteDelta] The minimum distance the tram must travel when randomly flying
   */
  constructor(options) {
    const spriteId = 'tram/' + options.colorProfile;
    const states = [
      {
        name: 'rainbow',
        animationSpeed: 0.1

      },
      {
        name: 'shot',
        animationSpeed: 0.5

      },
      {
        name: 'big',
        animationSpeed: 0.1

      }
    ];
    
    super(spriteId, options.spritesheet, states);
    this.alive = true;
    this.visible = true;
    this.options = options;
    this.anchor.set(0.5, 0.5);
    this.starts = [
      new Point(-100,110),
      new Point(-100,245),
      new Point(-100,380)
    ];
    this.destinations = [
      new Point(900,110),
      new Point(900,245),
      new Point(900,380)
    ];
  }

  /**
   * randomRoute
   * Method that causes the tram the randomly fly around a specific region of its parent
   * @param {Object} opts options for the flight tween
   * @param {Number} [opts.minX=0] Lowest x value allowed
   * @param {Number} [opts.maxX=Infinity] Highest x value allowed
   * @param {Number} [opts.minY=0] Lowest Y value allowed
   * @param {Number} [opts.maxY=Infinity] Highest Y value allowed
   * @param {Number} [opts.randomRouteDelta=300] Minimum distance to the next destination
   * @param {Number} [opts.speed=1] Speed of travel on a scale of 0 (slow) to 10 (fast)
   */
  randomRoute(opts) {
    const options = _extend({
      minX: 0,
      maxX: this.options.maxX || Infinity,
      minY: 0,
      maxY: this.options.maxY || Infinity,
      randomRouteDelta: this.options.randomRouteDelta || RANDOM_ROUTE_DELTA,
      speed: 1
    }, opts);

    let distance, destination;
    do {
      destination = {
        x: _random(options.minX, options.maxX),
        y: _random(options.minY, options.maxY)
      };
      distance = Utils.pointDistance(this.position, destination);
    } while (distance < options.randomRouteDelta);

    this.routeTo({
      point: destination,
      speed: options.speed,
      onComplete: this.randomRoute.bind(this, options)
    });
  }

    /**
   * randomTrack
   * Method that causes the tram the randomly fly around a specific region of its parent
   * @param {Object} opts options for the flight tween
   * @param {Number} [opts.speed=1] Speed of travel on a scale of 0 (slow) to 10 (fast)
   */
  randomTrack(opts) {
    const options = _extend({
      speed: 1
    }, opts);

    var track = _random(0,2);

    this.position.set(this.starts[track].x, this.starts[track].y);

    var destination = this.destinations[track];

    this.parent.setChildIndex(this, this.parent.children.length - 1);

    this.routeTo({
      point: destination,
      speed: options.speed,
      onComplete: () => {
            this.visible = false;
            this.position.set(-300,-300);
            //this.alive = false;
          }
    });
  }

  /**
   * routeTo
   * Method that adds an animation to the trams timeline for flying to a specified point.
   * @param opts
   * @param {PIXI.Point} [opts.point] Location the tram should go to
   * @param {Number} [opts.speed] Integer from 0 to 10 which determines how fast the tram flys
   * @param {Function} [opts.onStart=_noop] Method to call when the tram begins flying to the destination
   * @param {Function} [opts.onComplete_noop] Method to call when the tram has arrived at the destination
   * @returns {Tram}
   */
  routeTo(opts) {
    const options = _extend({
      point: this.position,
      speed: this.speed,
      onStart: _noop,
      onComplete: _noop
    }, opts);

    this.speed = options.speed;

    //const direction = Utils.directionOfTravel(this.position, options.point);
    //const direction = 'up'
    const tweenSeconds = (this.flightAnimationMs + _random(0, 300)) / 1000;

    //this.rotation = Utils.rotation(this.position, options.point);

    this.timeline.to(this.position, tweenSeconds, {
      x: options.point.x,
      y: options.point.y,
      ease: 'Linear.easeNone',
      onStart: () => {
        if (!this.alive) {
          this.stopAndClearTimeline();
        }
        this.play();
        this.state = 'big';
        options.onStart();
      },
      onComplete: options.onComplete
    });

    return this;
  }

  /**
   * shot
   * Method that animates the tram when the player shoots it
   */
  shot() {
    if (!this.alive) {
      return;
    }
    this.alive = false;
    
    this.rotation = 0;

    this.stopAndClearTimeline();
    this.timeline.add(() => {
      this.state = 'shot';
      sound.play('gunSound', _noop);
    });

    this.timeline.to(this.position, AWAY_ANIMATION_SECONDS, {
      ease: 'Linear.easeNone',
      delay: 0.3,
      onStart: () => {
        this.state = 'rainbow';
      },
      onComplete: () => {

        var options = {};
        options.endPoint = new Point(2000, this.position.y);

        this.timeline.to(this.position, 2, {
          x: options.endPoint.x,
          y: options.endPoint.y,
          ease: 'Linear.easeNone',
          onStart: () => {
            this.state = 'rainbow';
          },
          onComplete: () => {
            this.visible = false;
          }
        });
      }
    });

  }

  /**
   * isActive
   * Helper that tells whether the tram is currently or is able to be animated.
   * Because trams have a complex death sequence, this method checks if a tram is visible
   * in addition to the standard timeline animation check. This avoids potential race conditions
   * since in Tramhunt, if you can see the tram, it's beind animated in some way even if it's
   * technically "dead"
   * @returns {*|boolean}
   */
  isActive() {
    return this.visible || super.isActive();
  }

  /**
   * speed - getter
   * This method returns the
   * @returns {Number} Returns the speed level, a number from 0 to 10
   */
  get speed() {
    return this.speedVal;
  }

  /**
   * speed - setter
   * Method that determines how fast the tram should fly. Uses a 0-10 scale for ease and since
   * it technically "goes to 11"
   * @see https://www.youtube.com/watch?v=KOO5S4vxi0o.
   * @param {Number} val A number from 0 (slow) to 10 (fast) that sets the length of the flight tween
   */
  set speed(val) {
    let flightAnimationMs;
    switch (val) {
      case 0:
        flightAnimationMs = 2600;
        break;
      case 1:
        flightAnimationMs = 2400;
        break;
      case 2:
        flightAnimationMs = 2300;
        break;
      case 3:
        flightAnimationMs = 2100;
        break;
      case 4:
        flightAnimationMs = 1900;
        break;
      case 5:
        flightAnimationMs = 1700;
        break;
      case 6:
        flightAnimationMs = 1500;
        break;
      case 7:
        flightAnimationMs = 1300;
        break;
      case 8:
        flightAnimationMs = 1100;
        break;
      case 9:
        flightAnimationMs = 900;
        break;
      case 10:
        flightAnimationMs = 500;
        break;
    }
    this.speedVal = val;
    this.flightAnimationMs = flightAnimationMs;
  }
}

export default Tram;



// WEBPACK FOOTER //
// ./src/modules/Tram.js